import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MessageKey, useI18n } from '../i18n';
import { RootState } from '../store';

import { CardImage } from './art/CardImage';
import { PageShell } from './components/PageShell';
import { playCue } from './feedback';
import { interpretCard } from './interpret';
import { SPREADS } from './spreads';
import { deleteReading } from './tarotSlice';

/** Every reading that was revealed, newest first. Love readings carry the name they were for. */
export const HistoryPage: React.FC = () => {
    const { t, language, locale } = useI18n();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const history = useSelector((s: RootState) => s.tarot.history);
    const [deleting, setDeleting] = useState<string | null>(null);

    return (
        <PageShell to="/">
            <Typography variant="h4" component="h1" gutterBottom>
                {t('history.title')}
            </Typography>
            {history.length === 0 && (
                <Typography color="text.secondary">
                    {t('history.empty')}
                </Typography>
            )}
            <Stack spacing={1.5}>
                {history.map((r) => {
                    const theme = SPREADS[r.spread].theme;
                    return (
                        <Paper
                            key={r.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'rgba(21,14,36,.85)',
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                    playCue('ui');
                                    navigate(`/reading/${r.id}`);
                                }}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    border: 0,
                                    background: 'none',
                                    color: 'text.primary',
                                    font: 'inherit',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                }}
                            >
                                <Stack direction="row" sx={{ flexShrink: 0 }}>
                                    {r.cards.slice(0, 4).map((c, i) => (
                                        <Box
                                            key={c.id}
                                            sx={{
                                                width: 34,
                                                ml: i ? -1.5 : 0,
                                                boxShadow:
                                                    '0 2px 6px rgba(0,0,0,.6)',
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <CardImage
                                                cardId={c.id}
                                                reversed={c.reversed}
                                                name={
                                                    interpretCard(
                                                        c.id,
                                                        c.reversed,
                                                        theme,
                                                        language
                                                    ).name
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontFamily: 'Cinzel, serif',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {t(
                                            `spread.${r.spread}.name` as MessageKey
                                        )}
                                        {r.name && (
                                            <Box
                                                component="span"
                                                sx={{ color: 'primary.main' }}
                                            >
                                                {' '}
                                                {t('history.forName', {
                                                    name: r.name,
                                                })}
                                            </Box>
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {new Date(r.at).toLocaleString(locale, {
                                            dateStyle: 'medium',
                                            timeStyle: 'short',
                                        })}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                aria-label={t('history.delete')}
                                onClick={() => setDeleting(r.id)}
                            >
                                <DeleteOutlineRoundedIcon />
                            </IconButton>
                        </Paper>
                    );
                })}
            </Stack>
            <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
                <DialogTitle>{t('history.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('history.deleteBody')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleting(null)}>
                        {t('history.cancel')}
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => {
                            if (deleting) dispatch(deleteReading(deleting));
                            setDeleting(null);
                        }}
                    >
                        {t('history.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageShell>
    );
};
